/* eslint-disable complexity, sonarjs/cognitive-complexity, max-params, no-nested-ternary */
// Local-only fixtures for /talent-comparison/mock. Every request the page makes
// is answered from here so each UI state can be reviewed without a backend.
// Response keys are already camelCase (the real client converts before returning).
export const isTalentComparisonMock = () =>
  typeof window !== 'undefined' &&
  /^\/talent-comparison\/mock\/?$/.test(window.location.pathname);

const BASE = 'v1/analytics/talent_comparison';
const PERIOD = { startsAt: '2026-01-01', endsAt: '2026-12-31' };

// ───────────────────────── People & groups ─────────────────────────
// Each person exercises a different edge case, see `note`.
export const MOCK_PLACEMENTS = [
  {
    id: 1001,
    userId: 11,
    name: 'Ayu Pratama',
    jobTitle: 'Senior Product Designer',
    note: 'Complete data in every source and cycle',
    profilePicture: ''
  },
  {
    id: 1002,
    userId: 12,
    name: 'Bima Santoso',
    jobTitle: 'Backend Engineer',
    note: 'Sparse: gaps in sources, below expected, 0 level',
    profilePicture: ''
  },
  {
    id: 1003,
    userId: 13,
    name: 'Citra Kusumaningrum Wijayakusuma',
    jobTitle: 'Head of Customer Experience and Operational Excellence',
    note: 'Long name and title, above expected, no calibration',
    profilePicture: ''
  },
  {
    id: 1004,
    userId: 14,
    name: 'Dimas Anggara',
    jobTitle: 'Junior Analyst',
    note: 'Zero counts, not involved in later cycles, no goals',
    profilePicture: ''
  },
  {
    id: 1005,
    userId: 15,
    name: 'Eka Putri',
    jobTitle: 'People Operations Lead',
    note: 'Calibration categories, 10 level scale',
    profilePicture: ''
  }
];

const FILLER_NAMES = [
  'Farhan Hakim',
  'Gita Maharani',
  'Hendra Wijaya',
  'Indah Permata',
  'Joko Prasetyo',
  'Kirana Larasati',
  'Lukman Hidayat',
  'Maya Anindita'
];

const EMPLOYEES = [
  ...MOCK_PLACEMENTS,
  ...FILLER_NAMES.map((name, index) => ({
    id: 1006 + index,
    userId: 16 + index,
    name,
    jobTitle: ['Designer', 'Engineer', 'Analyst', 'Manager'][index % 4],
    profilePicture: ''
  }))
].map((p) => ({
  ...p,
  title: p.jobTitle,
  subtitle: p.jobTitle,
  userState: 'active',
  info: { address: 'Jakarta' },
  startsAt: '2022-03-01',
  endsAt: p.id === 1004 ? '2026-08-31' : undefined,
  userRelation: p.id === 1002 ? 'direct_report' : 'self'
}));

export const MOCK_GROUPS = [
  {
    id: 2001,
    name: 'Product & Design Squad',
    groupType: 'team',
    description: 'Product designers, researchers and PMs'
  },
  {
    id: 2002,
    name: 'Customer Success Jakarta',
    groupType: 'department',
    description: 'Customer success managers and support leads'
  },
  {
    id: 2003,
    name: 'Engineering',
    groupType: 'department',
    description: 'All engineers'
  }
];

const personOf = (id) => EMPLOYEES.find((e) => e.id === id);
const groupOf = (id) => MOCK_GROUPS.find((g) => g.id === id);

const targetInfo = (targetType, id) => {
  const t = targetType === 'group' ? groupOf(id) : personOf(id);
  return {
    id,
    userId: t?.userId,
    type: targetType,
    name: t?.name || `Target ${id}`,
    profilePicture: t?.profilePicture || '',
    title: t?.title || t?.description || '',
    ...PERIOD,
    behaviorGroupId: 1,
    behaviorGroupName: 'Company Behaviors'
  };
};

// ───────────────────────── Behavior ─────────────────────────
const BEHAVIORS = {
  value: [
    ['Integrity', 'Acts honestly and does what is right.'],
    ['Teamwork', 'Collaborates and supports teammates.'],
    ['Customer Obsession', 'Starts from what the customer needs.'],
    ['Ownership', 'Takes responsibility for outcomes.'],
    [
      'Continuously learns, adapts and shares knowledge across teams in a rapidly changing environment',
      'Long title: checks truncation in the sidebar cell.'
    ]
  ],
  competency: [
    ['Problem Solving', 'Breaks down ambiguity into clear steps.'],
    ['Communication', 'Shares information clearly.'],
    ['Leadership', 'Guides others toward a shared goal.'],
    ['Technical Excellence', 'Builds high quality solutions.'],
    ['Stakeholder Management', 'Aligns expectations with partners.'],
    ['Data Literacy', 'Uses data to make decisions.']
  ]
};

// [current, expected, max]; null means the person has no data on that aspect.
const PROFICIENCY = {
  value: {
    1001: [
      [3, 3, 5],
      [4, 3, 5],
      [2, 3, 5],
      [5, 4, 5],
      [3, 3, 5]
    ],
    1002: [[1, 3, 5], null, [0, 2, 5], [2, 2, 3], null],
    1003: [[5, 4, 5], [4, 4, 5], [5, 5, 5], null, [4, 3, 5]],
    1004: [[2, 2, 4], [2, 3, 4], null, [1, 2, 4], [3, 2, 4]],
    1005: [
      [3, 4, 10],
      [7, 6, 10],
      [10, 10, 10],
      [0, 5, 10],
      [4, 4, 10]
    ],
    2001: [
      [3.4, 3, 5],
      [3.8, 3.5, 5],
      [2.6, 3, 5],
      [4.1, 4, 5],
      [3, 3, 5]
    ],
    2002: [[2.2, 3, 5], [3, 3, 5], null, [3.5, 3, 5], [2.9, 3, 5]]
  },
  competency: {
    1001: [
      [4, 3, 5],
      [3, 3, 5],
      [2, 4, 5],
      [5, 5, 5],
      [3, 3, 5],
      [2, 3, 5]
    ],
    1002: [[2, 3, 5], [1, 3, 5], null, [3, 3, 5], null, [0, 2, 5]],
    1003: [[5, 4, 5], [5, 4, 5], [5, 5, 5], [4, 4, 5], [5, 4, 5], null],
    1004: [null, [2, 2, 4], [1, 3, 4], [2, 2, 4], null, [3, 2, 4]],
    1005: [
      [6, 5, 10],
      [8, 7, 10],
      [4, 7, 10],
      [9, 8, 10],
      [5, 5, 10],
      [2, 6, 10]
    ],
    2001: [
      [3.2, 3, 5],
      [3.6, 3, 5],
      [3, 3.5, 5],
      [4.2, 4, 5],
      [3.1, 3, 5],
      [2.8, 3, 5]
    ],
    2002: [[3, 3, 5], [2.5, 3, 5], [2.9, 3, 5], null, [3.3, 3, 5], [2.1, 3, 5]]
  }
};

const ratingFor = (current, max) => {
  const score = Math.round((current / max) * 5 * 100) / 100;
  if (score >= 4.5) return { score, status: 'Outstanding', colorHex: '22C55E' };
  if (score >= 3.5) return { score, status: 'Exceeds', colorHex: '3B82F6' };
  if (score >= 2.5) return { score, status: 'Meets', colorHex: 'EAB308' };
  if (score >= 1.5)
    return { score, status: 'Needs Improvement', colorHex: 'F97316' };
  return { score, status: 'Unsatisfactory', colorHex: 'EF4444' };
};

const countsFor = (targetId, behaviorIndex, isGroup) => {
  if (targetId === 1004) return { recognitionCount: 0, feedbackCount: 0 };
  if (targetId === 1003 && behaviorIndex === 0)
    return { recognitionCount: 128, feedbackCount: 0 };
  const recognitionCount = (targetId * 7 + behaviorIndex * 3) % 9;
  const feedbackCount = (targetId * 5 + behaviorIndex * 2) % 5;
  return isGroup
    ? {
        recognitionCount: recognitionCount + 0.5,
        feedbackCount: feedbackCount + 0.25
      }
    : { recognitionCount, feedbackCount };
};

const behaviorAssessment = (
  behaviorType,
  targetType,
  targetId,
  params = {}
) => {
  const source = params.behaviorComparisonType || 'proficiency_levels';
  const isGroup = targetType === 'group';
  const proficiency = PROFICIENCY[behaviorType]?.[targetId] || [];
  const behaviors = BEHAVIORS[behaviorType].flatMap(
    ([title, description], index) => {
      const base = {
        id: (behaviorType === 'value' ? 100 : 200) + index,
        title,
        description,
        currentLevel: 0,
        expectedLevel: 0,
        maxLevel: 0,
        score: 0,
        colorHex: '',
        status: '',
        feedbackCount: 0,
        recognitionCount: 0
      };
      const level = proficiency[index];

      if (source === 'proficiency_levels') {
        if (!level) return [];
        const [currentLevel, expectedLevel, maxLevel] = level;
        return [{ ...base, currentLevel, expectedLevel, maxLevel }];
      }
      if (source === 'performance_review_rating') {
        if (!level || (targetId === 1002 && index % 3 === 2)) return [];
        return [{ ...base, ...ratingFor(level[0], level[2]) }];
      }
      // 360 feedback (coaching) and continuous feedback
      if (targetId === 1002 && index % 2 === 1 && source === '360_feedback')
        return [];
      return [{ ...base, ...countsFor(targetId, index, isGroup) }];
    }
  );

  return {
    ...targetInfo(targetType, targetId),
    cycleName: 'H1 2026 Performance Review',
    behaviors
  };
};

const SIDEBAR_ACTORS = [
  ['Rina Kartika', 'Product Manager'],
  ['Yoga Pranata', 'Engineering Manager'],
  ['Salsa Nabila', 'Designer'],
  ['Tegar Mahendra', 'QA Engineer']
];

const sidebarAssessments = (source, offset = 0, limit = 5) => {
  const isFeedback =
    source === '360_feedback' || source === 'continuous_feedback';
  const total = 9;
  const items = Array.from({ length: total }, (_, index) => {
    const [name, jobTitle] = SIDEBAR_ACTORS[index % SIDEBAR_ACTORS.length];
    const cycle =
      index < 5
        ? { id: 3001, name: 'H1 2026 Performance Review' }
        : { id: 3002, name: 'H2 2025 Performance Review' };
    const isRecognition = index % 3 !== 2;
    return {
      type: isFeedback
        ? isRecognition
          ? 'recognition'
          : 'feedback'
        : 'performance_review_answer',
      id: 9000 + index,
      createdAt: `2026-0${(index % 8) + 1}-1${index % 9}T09:30:00.000Z`,
      actor: {
        id: 500 + index,
        name,
        jobTitle,
        email: `${name.split(' ')[0].toLowerCase()}@example.com`,
        profilePicture: ''
      },
      cycle,
      phaseType: ['manager_review', 'peer_review', 'self_review'][index % 3],
      objective:
        isFeedback && index % 2 === 0
          ? { id: 700 + index, name: 'Launch the new onboarding experience' }
          : null,
      score: isFeedback ? null : 3 + (index % 3),
      statusName: isFeedback
        ? null
        : ['Meets', 'Exceeds', 'Outstanding'][index % 3],
      comment:
        index === 1
          ? 'Short note.'
          : index === 4
            ? '**Strong** delivery this period.\n\n- Led the discovery workshop\n- Unblocked two teams\n\nCould improve on documenting decisions earlier so the wider team stays in sync, especially when priorities shift mid-sprint and several stakeholders need context quickly.'
            : 'Consistently demonstrates this behavior and supports teammates when it matters.'
    };
  });
  const page = items.slice(offset, offset + limit);
  const nextOffset = offset + limit;
  return {
    data: page,
    pagination: nextOffset < total ? { nextOffset } : { nextOffset: null }
  };
};

// ───────────────────────── Goals ─────────────────────────
const GOAL_COUNTS = {
  1001: { goal: 6, project: 2, task: 4 },
  1002: { goal: 1, project: 0, task: 0 },
  1003: { goal: 12, project: 3, task: 15 },
  1004: { goal: 0, project: 0, task: 0 },
  1005: { goal: 3, project: 1, task: 2 },
  2001: { goal: 24, project: 6, task: 31 },
  2002: { goal: 8, project: 0, task: 11 },
  2003: { goal: 40, project: 12, task: 60 }
};

const PROGRESS = [
  ['44DB5E', 100, 'On track'],
  ['22C55E', 82.5, 'On track'],
  ['F59E0B', 46, 'Behind'],
  ['EF4444', 12, 'At risk'],
  ['6D3AF8', 0, 'Not started']
];

const measurement = (index, objectiveId) => {
  const [progressColorHex, progressPercentage] =
    PROGRESS[index % PROGRESS.length];
  return {
    id: objectiveId,
    unitId: 1,
    description: '',
    unit: '%',
    defaultRollUp: 'sum',
    rollUp: 'sum',
    startingValue: 0,
    currentValue: progressPercentage,
    progressPercentage,
    targetValue: 100,
    progressColor: index % PROGRESS.length,
    progressColorHex,
    expectedProgressValue: 60,
    expectedProgressPercentage: 60
  };
};

const GOAL_NAMES = [
  'Increase activation rate for new workspaces',
  'Ship the redesigned onboarding flow end to end across web and mobile, including analytics, localisation, accessibility review and rollout plan',
  'Reduce p95 API latency',
  'Raise customer satisfaction score above 4.5',
  'Document the design system'
];

const objectiveList = (params = {}, targetType, targetId) => {
  const limit = Number(params.limit) || 10;
  const types = [].concat(params.type || []);
  let kind = 'goal';
  if (params.parentId) kind = 'child-task';
  else if (params.isProject === true) kind = 'project';
  else if (types.includes('task')) kind = 'task';

  const counts = GOAL_COUNTS[targetId] || { goal: 0, project: 0, task: 0 };
  const total =
    kind === 'goal'
      ? counts.goal
      : kind === 'project'
        ? counts.project
        : kind === 'task'
          ? counts.task
          : 14;

  const offset = Number(params.olderThan) || 0;
  const rows = Array.from({ length: Math.min(total, 200) }, (_, index) => {
    const id =
      {
        goal: 10000,
        project: 20000,
        task: 30000,
        'child-task': 40000
      }[kind] + index;
    return {
      id,
      name:
        kind === 'task' || kind === 'child-task'
          ? `Task ${index + 1}: ${GOAL_NAMES[index % GOAL_NAMES.length]}`
          : GOAL_NAMES[index % GOAL_NAMES.length],
      type: kind === 'child-task' ? 'task' : kind,
      involvementAs: ['owner', 'leader', 'assignee', 'collaborator'][index % 4],
      measurement: measurement(index, id)
    };
  });
  const page = rows.slice(offset, offset + limit);
  const next = offset + limit;
  return {
    data: page,
    pagination:
      next < rows.length ? { next: { olderThan: next } } : { next: null }
  };
};

// ───────────────────────── Performance review ─────────────────────────
const ALL_CYCLES = [
  { id: 3001, name: 'H1 2026 Performance Review' },
  { id: 3002, name: 'H2 2025 Performance Review' },
  { id: 3003, name: 'Q1 2026 Probation Review' },
  { id: 3004, name: 'H1 2025 Performance Review' },
  { id: 3005, name: 'H2 2024 Performance Review' },
  { id: 3006, name: 'H1 2024 Performance Review' },
  { id: 3007, name: 'Annual Leadership 360 Review 2023' },
  { id: 3008, name: 'Mid-year Check-in 2023' }
];

const INVOLVED = {
  1001: [3001, 3002, 3003, 3004, 3005, 3006, 3007, 3008],
  1002: [3001, 3002],
  1003: [3001, 3003],
  1004: [3001],
  1005: [3001, 3002, 3003]
};

const SCALE = [
  ['Unsatisfactory', 'EF4444'],
  ['Needs Improvement', 'F97316'],
  ['Meets', 'EAB308'],
  ['Exceeds', '3B82F6'],
  ['Outstanding', '22C55E']
];

const option = (value, extra = {}) => ({
  id: 800 + value,
  option: SCALE[value - 1][0],
  name: SCALE[value - 1][0],
  value,
  score: value,
  colorHex: SCALE[value - 1][1],
  description: '',
  secondaryIconUrl: '',
  bottomThresholdScore: value - 0.5,
  upperThresholdScore: value + 0.5,
  ...extra
});

const user = (index) => {
  const [name, jobTitle] = SIDEBAR_ACTORS[index % SIDEBAR_ACTORS.length];
  return {
    id: 500 + index,
    name,
    jobTitle,
    email: `${name.split(' ')[0].toLowerCase()}@example.com`,
    profilePicture: ''
  };
};

const SHOW_OPTIONS = ['score_and_label', 'score_only', 'label_only'];

const reviewResult = (cycleId, targetId) => {
  const involved = (INVOLVED[targetId] || []).includes(cycleId);
  const cycle = ALL_CYCLES.find((c) => c.id === cycleId);
  const base = {
    ...targetInfo('placement', targetId),
    cycleId,
    cycleName: cycle?.name,
    isInvolved: involved
  };
  if (!involved) return base;

  const person = personOf(targetId);
  const isCategories = targetId === 1005;
  const hasCalibration = targetId !== 1003 && cycleId === 3001;
  const seed = (targetId + cycleId) % 5;
  const finalValue = Math.min(5, Math.max(1, seed + 1));
  const [finalLabel, finalColor] = SCALE[finalValue - 1];
  const showOption = SHOW_OPTIONS[(targetId + cycleId) % 3];

  const isProbation = cycleId === 3003;
  const questions = isProbation
    ? []
    : [
        {
          question: {
            id: 501,
            question: 'How effectively does the employee collaborate?',
            description: 'Consider cross-team work this period.',
            questionType: 'rating',
            optionsLabelVisibility: true
          },
          phases: [
            {
              id: 1,
              phaseType: 'self_review',
              answers: [
                {
                  comment:
                    'I paired with engineering weekly to keep scope realistic.',
                  user: {
                    ...user(0),
                    name: person?.name,
                    jobTitle: person?.jobTitle
                  },
                  options: [option(4)]
                }
              ]
            },
            {
              id: 2,
              phaseType: 'manager_review',
              answers: [
                {
                  comment: '',
                  user: user(1),
                  options: [option(3)]
                }
              ]
            },
            {
              id: 3,
              phaseType: 'peer_review',
              answers: [
                {
                  comment:
                    'A reliable partner who communicates early. Occasionally takes on too much and could delegate more, which would free time for the quality work they are best at.',
                  user: user(2),
                  options: [option(5)]
                },
                {
                  comment: 'Great to work with.',
                  user: user(3),
                  options: [option(2)]
                }
              ]
            }
          ]
        },
        {
          question: {
            id: 502,
            question: 'Which area had the greatest impact?',
            description: '',
            questionType: 'multiple_choices',
            optionsLabelVisibility: false
          },
          phases: [
            {
              id: 4,
              phaseType: 'manager_review',
              answers: [
                {
                  comment: 'Shipping the onboarding revamp unblocked growth.',
                  user: user(1),
                  options: [{ id: 901, option: 'Product delivery', value: 1 }]
                }
              ]
            }
          ]
        },
        {
          question: {
            id: 503,
            question: 'Which development areas should we prioritize?',
            description: 'Pick all that apply.',
            questionType: 'multiple_selections',
            optionsLabelVisibility: false
          },
          phases: [
            {
              id: 5,
              phaseType: 'manager_review',
              answers: [
                {
                  comment: '',
                  user: user(1),
                  options: [
                    { id: 911, option: 'Strategic thinking', value: 1 },
                    { id: 912, option: 'Mentoring', value: 2 },
                    {
                      id: 913,
                      option: 'Public speaking and facilitation',
                      value: 3
                    }
                  ]
                }
              ]
            },
            {
              id: 6,
              phaseType: 'self_review',
              answers: [
                {
                  comment: 'Would like more exposure to leading workshops.',
                  user: { ...user(0), name: person?.name },
                  options: [{ id: 912, option: 'Mentoring', value: 2 }]
                }
              ]
            }
          ]
        },
        {
          question: {
            id: 504,
            question: 'Share any additional context',
            description: '',
            questionType: 'comments',
            optionsLabelVisibility: false
          },
          phases: [
            {
              id: 7,
              phaseType: 'manager_review',
              answers: [
                {
                  comment:
                    '## Highlights\n\n1. Led the redesign\n2. Mentored two juniors\n\n> Keep it up.',
                  user: user(1),
                  options: []
                }
              ]
            }
          ]
        }
      ];

  const sidebarQuestions = questions.map(({ question }) => ({
    id: question.id,
    question: question.question,
    questionType: question.questionType,
    description: question.description,
    config: { optionsLabelVisibility: question.optionsLabelVisibility }
  }));

  return {
    ...base,
    reviewScore: {
      performanceCategory: {
        performanceCategory: isCategories
          ? ['Top Talent', 'Core Contributor', 'Needs Support'][seed % 3]
          : finalLabel,
        statusName: finalLabel,
        statusColorHex: finalColor,
        showOption
      },
      final: {
        score: targetId === 1002 && cycleId === 3002 ? null : finalValue + 0.25,
        statusName: finalLabel,
        statusColorHex: finalColor,
        showOption
      },
      scoreAspects: [
        {
          id: 11,
          name: 'Goals',
          score: 3.5,
          statusName: 'Exceeds',
          statusColorHex: '3B82F6',
          showOption: 'score_and_label'
        },
        {
          id: 12,
          name: 'Behavior',
          score: 2.25,
          statusName: 'Needs Improvement',
          statusColorHex: 'F97316',
          showOption: 'score_only'
        },
        {
          id: 13,
          name: 'Values',
          score: null,
          statusName: 'Meets',
          statusColorHex: 'EAB308',
          showOption: 'label_only'
        }
      ],
      lastScoreHistoryComment: ''
    },
    questions,
    sidebar: {
      reviewAspects: [
        { id: -1, name: 'Final' },
        { id: 11, name: 'Goals' },
        { id: 12, name: 'Behavior' },
        { id: 13, name: 'Values' }
      ],
      questions: sidebarQuestions,
      initialCalibrationAspect: hasCalibration
        ? {
            id: 99,
            aspectType: 'initial_calibration',
            name: 'Initial Calibration'
          }
        : undefined,
      hasCalibrationHistory: hasCalibration && targetId !== 1004
    },
    calibration: {
      id: cycleId,
      config: {
        calibrationMechanism: isCategories
          ? 'calibration_categories'
          : 'rating_score',
        quota: 'none',
        disclosure: 'all',
        deliverable: true,
        showDueDate: false,
        notifyOnStart: false,
        commentRequired: false,
        maxManagerLevel: 2,
        revokeCalibratorIds: [],
        allowViewTargetResult: true,
        sidebarEvidenceEnabled: false,
        forcedDistributionConfig: null,
        forcedDistributionEnabled: false,
        revieweeSummaryVisibility: 'all',
        reverseOrderScoringMarksEnabled: false,
        notifyRevieweeOnCalibrationEnded: false
      },
      ratingTemplate: { scoringComponents: [{ scoringMarks: [] }] }
    },
    initialCalibrationAnswer: hasCalibration
      ? targetId === 1002
        ? undefined
        : {
            comment: 'Initial placement before cross-team normalisation.',
            phaseType: 'calibration',
            score: 3,
            status: 'done',
            user: user(1),
            option: option(3)
          }
      : undefined,
    currentCalibrationCard: false,
    downloadResultPdf: targetId !== 1003
  };
};

const calibrationHistories = (targetId, params = {}) => {
  const total = targetId === 1001 ? 5 : targetId === 1005 ? 2 : 1;
  const limit = Number(params.limit) || 3;
  const all = Array.from({ length: total }, (_, index) => {
    const iteration = total - 1 - index;
    const value = Math.min(5, Math.max(1, 2 + iteration));
    return {
      id: targetId * 10 + iteration,
      calibrationId: 1,
      calibrationOwner: 'HR',
      calibrator: iteration === 0 ? null : user(iteration),
      comment:
        iteration === 2
          ? 'Moved up after reviewing delivery evidence from the two launches.'
          : '',
      createdAt: `2026-0${iteration + 1}-15T10:00:00.000Z`,
      updatedAt: `2026-0${iteration + 1}-15T10:00:00.000Z`,
      isCalibrate: iteration !== 0,
      performanceCategory: ['Needs Support', 'Core Contributor', 'Top Talent'][
        iteration % 3
      ],
      statusColorHex: SCALE[value - 1][1],
      statusName: SCALE[value - 1][0],
      score: value,
      iteration
    };
  }).sort((a, b) => b.id - a.id);

  const olderThan = Number(params.olderThan);
  const remaining = olderThan ? all.filter((row) => row.id < olderThan) : all;
  const page = remaining.slice(0, limit);
  const hasMore = remaining.length > limit;
  return {
    data: page,
    pagination: {
      next: hasMore ? { olderThan: page[page.length - 1].id } : null,
      prev: null
    }
  };
};

// ───────────────────────── Router ─────────────────────────
const paginate = (rows, params = {}, key = 'olderThan') => {
  const limit = Number(params.limit) || 10;
  const offset = Number(params[key]) || 0;
  const page = rows.slice(offset, offset + limit);
  const next = offset + limit;
  return {
    data: page,
    pagination: { next: next < rows.length ? { [key]: next } : null }
  };
};

const matchSearch = (rows, q) =>
  q
    ? rows.filter((r) => r.name.toLowerCase().includes(String(q).toLowerCase()))
    : rows;

export const getTalentComparisonMockResponse = (endpoint, { params } = {}) => {
  if (!isTalentComparisonMock()) return null;

  let m;

  if (endpoint === `${BASE}/placements`)
    return paginate(matchSearch(EMPLOYEES, params?.q), params);

  if (endpoint === `${BASE}/groups`)
    return paginate(matchSearch(MOCK_GROUPS, params?.q), params);

  if (
    (m = endpoint.match(
      /behavior_assessments\/(value|competency)\/targets\/(\w+)\/(\d+)$/
    ))
  )
    return { data: behaviorAssessment(m[1], m[2], Number(m[3]), params) };

  if (/behavior_assessments\/behaviors\/\d+\/targets\//.test(endpoint))
    return sidebarAssessments(
      params?.behaviorComparisonType,
      Number(params?.offset) || 0,
      Number(params?.limit) || 5
    );

  if ((m = endpoint.match(/objectives\/targets\/(\w+)\/(\d+)\/count$/)))
    return {
      data: {
        ...targetInfo(m[1], Number(m[2])),
        objectivesCount: GOAL_COUNTS[Number(m[2])] || {
          goal: 0,
          project: 0,
          task: 0
        }
      }
    };

  if ((m = endpoint.match(/objectives\/targets\/(\w+)\/(\d+)\/objects$/)))
    return objectiveList(params, m[1], Number(m[2]));

  if (endpoint === `${BASE}/performance_reviews/cycles`) {
    const rows = matchSearch(ALL_CYCLES, params?.search);
    const limit = Number(params?.limit) || 5;
    const offset = Number(params?.offset) || 0;
    const next = offset + limit;
    return {
      data: rows.slice(offset, next),
      pagination: { nextOffset: next < rows.length ? next : null }
    };
  }

  if (
    (m = endpoint.match(
      /performance_reviews\/cycles\/(\d+)\/targets\/placement\/(\d+)\/score_histories$/
    ))
  )
    return calibrationHistories(Number(m[2]), params);

  if (
    (m = endpoint.match(
      /performance_reviews\/cycles\/(\d+)\/targets\/(\w+)\/(\d+)$/
    ))
  )
    return { data: reviewResult(Number(m[1]), Number(m[3])) };

  if ((m = endpoint.match(/^v3\/objectives\/(\d+)\/children_count$/)))
    return {
      data: {
        childrenCount:
          Number(m[1]) >= 20000 && Number(m[1]) < 30000
            ? (Number(m[1]) % 4) + 2
            : 0
      }
    };

  return null;
};
